export const TENDER_DISTRICTS = [
  "中西區", "灣仔區", "東區", "南區", "油尖旺區", "深水埗區", "九龍城區",
  "黃大仙區", "觀塘區", "葵青區", "荃灣區", "屯門區", "元朗區", "北區", "大埔區",
  "沙田區", "西貢區", "離島區",
];
export type TenderTimeKey = "all" | "past7days" | "past30days" | "past90days" | "future90days" | "future30days" | "future7days" | "2026" | "2025" | "2024" | "2023" | "2022" | "2021" | "2020before";
const timeLabels: Record<TenderTimeKey, string> = {
  all: "全部日期", past7days: "過去7日", past30days: "過去30日", past90days: "過去90日",
  future7days: "未來7日", future30days: "未來30日", future90days: "未來90日",
  "2026": "2026年", "2025": "2025年", "2024": "2024年", "2023": "2023年", "2022": "2022年", "2021": "2021年", "2020before": "2020年或之前",
};
export interface TenderListingFilters {
  categories: string[]; districts: string[]; status: "open" | "closed" | "all";
  time: TenderTimeKey; search: string; page: number;
}
function values(params: URLSearchParams, name: string) {
  return [...new Set(params.getAll(name).flatMap(value => value.split(",")).map(value => value.trim().slice(0, 100)).filter(Boolean))].sort().slice(0, 30);
}
export function parseTenderListingFilters(params: URLSearchParams): TenderListingFilters {
  const status = params.get("status");
  const time = params.get("time");
  return {
    categories: values(params, "category"), districts: values(params, "district"),
    status: status === "closed" || status === "all" ? status : "open",
    time: time && Object.prototype.hasOwnProperty.call(timeLabels, time) ? time as TenderTimeKey : "future90days",
    search: (params.get("q") ?? params.get("search") ?? "").trim().slice(0, 200),
    page: Math.floor(Math.max(1, Math.min(10000, Number(params.get("page")) || 1))),
  };
}
export function tenderListingParams(filters: TenderListingFilters) {
  const params = new URLSearchParams();
  if (filters.categories.length) params.set("category", [...new Set(filters.categories)].sort().join(","));
  if (filters.districts.length) params.set("district", [...new Set(filters.districts)].sort().join(","));
  if (filters.status !== "open") params.set("status", filters.status);
  if (filters.time !== "future90days") params.set("time", filters.time);
  if (filters.search.trim()) params.set("q", filters.search.trim().slice(0, 200));
  if (filters.page > 1) params.set("page", String(filters.page));
  return params;
}
export function getTenderDateRange(key: TenderTimeKey, now = new Date()): { dateFrom?: string; dateTo?: string } {
  // Use Hong Kong calendar dates for both initial HTML and the interactive API.
  const today = new Date(new Date(now.getTime() + 8 * 3600_000).toISOString().slice(0, 10) + "T00:00:00Z");
  const day = (value: Date) => value.toISOString().slice(0, 10) + "T00:00:00Z";
  const match = key.match(/^(past|future)(7|30|90)days$/);
  if (match) {
    const boundary = new Date(today);
    boundary.setUTCDate(today.getUTCDate() + Number(match[2]) * (match[1] === "future" ? 1 : -1));
    return match[1] === "future" ? { dateFrom: day(today), dateTo: day(boundary) } : { dateFrom: day(boundary), dateTo: day(today) };
  }
  if (/^202[1-6]$/.test(key)) return { dateFrom: `${key}-01-01T00:00:00Z`, dateTo: `${key}-12-31T23:59:59Z` };
  if (key === "2020before") return { dateTo: "2020-12-31T23:59:59Z" };
  return {};
}
export function tenderListingSeo(filters: TenderListingFilters, categories: { slug: string; nameChinese: string }[] = []) {
  filters = { ...filters, search: filters.search.trim().slice(0, 200) };
  const categoryNames = filters.categories.map(slug => slug === "it" ? "IT／資訊科技" : categories.find(c => c.slug === slug)?.nameChinese ?? "指定類別");
  const districts = filters.districts.map(d => TENDER_DISTRICTS.includes(d) ? d : "指定地區");
  const place = districts.length ? districts.join("、") : "香港";
  const subject = `${place}學校${categoryNames.length ? ` ${categoryNames.join("、")}` : ""}招標${!districts.length && !categoryNames.length ? "列表" : ""}`;
  const status = filters.status === "open" ? "招標中" : filters.status === "closed" ? "已截止" : "全部狀態";
  const heading = subject + (filters.search ? ` — 搜尋「${filters.search}」` : "") + (filters.status !== "open" ? `｜${status}` : "");
  const title = heading + (filters.page > 1 ? `｜第 ${filters.page} 頁` : "");
  const deadline = filters.time === "all" ? "不限截止日期" : `${timeLabels[filters.time]}截止`;
  const summary = `瀏覽${subject}及書面報價公開摘要，${status}，${deadline}。可查看標題、地區、分類、截止日期及狀態；招標學校身份、聯絡資料、來源及文件僅供付費會員查看。`;
  const query = tenderListingParams(filters).toString();
  return { title, heading, description: (filters.search ? `搜尋「${filters.search}」。` : "") + summary,
    canonical: `/tenders${query ? `?${query}` : ""}`, summary };
}
